/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/config/endpoints.ts
 * PURPOSE: Configuration and environment settings
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

export const API_ENDPOINTS = {
  // ✅ WORKING ENDPOINTS
  auth: {
    login: '/auth/login',           // ✅ Working
    logout: '/auth/logout',         // ✅ Working  
    me: '/auth/me',                // ✅ Working
    refresh: '/auth/refresh',      // ⚠️ Needs fix
    register: '/auth/register',    // Not tested
  },
  
  attendance: {
    list: '/attendance',            // ✅ Working
    history: '/attendance/history', // ✅ Working
    clockIn: '/attendance/clock-in', // ✅ Working
    clockOut: '/attendance/clock-out', // ✅ Working
    today: null,                   // ❌ Not implemented
    stats: '/attendance/stats',    // ⚠️ Validation issues
  },
  
  schedules: {
    list: '/schedules',             // ✅ Working
    mySchedule: '/schedules/my-schedule', // ⚠️ Validation issues
    today: '/schedules/today',     // ⚠️ Validation issues
    week: '/schedules/week',       // ⚠️ Validation issues
    create: '/schedules',           // Not tested
    update: '/schedules/{id}',     // Not tested
  },
  
  tasks: {
    list: '/tasks',                 // ❌ 500 Error
    myTasks: '/tasks/my-tasks',    // ✅ Working
    pending: '/tasks/pending',     // ⚠️ Validation issues
    completed: '/tasks/completed', // ⚠️ Validation issues
    create: '/tasks',              // ❌ 500 Error
    update: '/tasks/{id}',         // Not tested
  },
  
  users: {
    list: '/users',                 // ❌ 500 Error
    me: '/users/me',               // ⚠️ Validation issues
    profile: '/users/profile',     // ⚠️ Validation issues
    update: '/users/me',           // ⚠️ Validation issues
  },
  
  organizations: {
    list: '/organizations',         // ✅ Working
    branches: '/organizations/branches', // ⚠️ Validation issues
  },
  
  // Production endpoints only
  shifts: '/schedules/shifts',      // Real endpoint mapping
  departments: '/organizations/departments',  // Real endpoint mapping
  locations: '/organizations/locations',      // Real endpoint mapping
  notifications: '/notifications',            // Real endpoint mapping
  metrics: '/metrics/dashboard',              // Real endpoint mapping
  version: '/version',                        // Real endpoint mapping
} as const;

// Feature flags based on endpoint availability
export const FEATURES = {
  SHIFTS_ENABLED: false,
  NOTIFICATIONS_ENABLED: false,
  DEPARTMENTS_ENABLED: false,
  LOCATIONS_ENABLED: false,
  ADVANCED_STATS_ENABLED: false,
  USER_MANAGEMENT_ENABLED: false, // Disabled due to 500 errors
  TASK_CREATION_ENABLED: false,   // Disabled due to 500 errors
};

// Helper to check if endpoint is available
export function isEndpointAvailable(endpoint: string | null): boolean {
  return endpoint !== null;
}

// Helper to get working endpoints only
export function getWorkingEndpoints() {
  return {
    auth: {
      login: API_ENDPOINTS.auth.login,
      logout: API_ENDPOINTS.auth.logout,
      me: API_ENDPOINTS.auth.me,
    },
    attendance: {
      list: API_ENDPOINTS.attendance.list,
      history: API_ENDPOINTS.attendance.history,
      clockIn: API_ENDPOINTS.attendance.clockIn,
      clockOut: API_ENDPOINTS.attendance.clockOut,
    },
    schedules: {
      list: API_ENDPOINTS.schedules.list,
    },
    tasks: {
      myTasks: API_ENDPOINTS.tasks.myTasks,
    },
    organizations: {
      list: API_ENDPOINTS.organizations.list,
    },
  };
}